import { DropdownTreeSelect, DropdownTreeSelectProps, TreeNodeProps } from './DropdownTreeSelect'
import { CarrierHubResponse } from '../../hooks/useCarrierHubList'
import { memo, useMemo } from 'react'
import { isArray } from '@apollo/client/utilities'
import { TreeNode } from 'react-dropdown-tree-select'

export interface CarrierHubTreeDropdownProps
  extends Omit<DropdownTreeSelectProps, 'data' | 'onChange'> {
  carrierHubs: CarrierHubResponse[]
  selectedCarrierHubs: string
  onChange: (selectedCarrierHubs: string) => void
}

const makeCarrierHubTree = (
  carrierHubs: CarrierHubResponse[],
  selectedCarrierHubs: string
): TreeNodeProps[] => {
  const selectedCarrierHubsArray = selectedCarrierHubs
    ? isArray(selectedCarrierHubs)
      ? selectedCarrierHubs
      : selectedCarrierHubs.split(',')
    : []
  const sortByValue = (a, b) => a.value.localeCompare(b.value)

  const makeChildren = (children: TreeNodeProps[] = [], carrierHub, checked) =>
    [
      ...children,
      {
        label: carrierHub.hubCode,
        value: `${carrierHub.carrierAccountCode}-${carrierHub.hubCode}`,
        tagLabel: `${carrierHub.carrierAccountCode} - ${carrierHub.hubCode}`,
        carrierHubs: [
          {
            carrierAccountCode: carrierHub.carrierAccountCode,
            hubCode: carrierHub.hubCode,
          },
        ],
        checked,
      },
    ].sort(sortByValue)

  // @ts-ignore
  return carrierHubs
    .reduce((acc, carrierHub) => {
      const carrier = acc.find((it) => it.value === carrierHub.carrierAccountCode)
      const checked = !!selectedCarrierHubsArray?.find(
        (it) => it === `${carrierHub.carrierAccountCode}-${carrierHub.hubCode}`
      )
      if (carrier) {
        carrier.children = makeChildren(carrier.children, carrierHub, checked)
        carrier.carrierHubs = [
          ...carrier.carrierHubs,
          {
            carrierAccountCode: carrierHub.carrierAccountCode,
            hubCode: carrierHub.hubCode,
          },
        ]
        return acc
      }
      acc.push({
        label: `${carrierHub.carrierAccountCode} - ${carrierHub.carrierName}`,
        value: carrierHub.carrierAccountCode,
        tagLabel: `${carrierHub.carrierAccountCode} - All Hubs`,
        carrierHubs: [
          {
            carrierAccountCode: carrierHub.carrierAccountCode,
            hubCode: carrierHub.hubCode,
          },
        ],
        children: makeChildren([], carrierHub, checked),
      })
      return acc
    }, [] as TreeNodeProps[])
    .sort(sortByValue)
}

function arePropsEqual(oldProps, newProps): boolean {
  return (
    oldProps.carrierHubs.length === newProps.carrierHubs.length &&
    oldProps.selectedCarrierHubs === newProps.selectedCarrierHubs
  )
}

const _onChangeFactory = (onChange) => (_: TreeNode, selectedNodes: TreeNode[]) => {
  const localSelectedCarrierHubs = new Set()
  selectedNodes.forEach((selectedNode) => {
    selectedNode.carrierHubs?.forEach((carrierHub) => {
      localSelectedCarrierHubs.add(carrierHub.carrierAccountCode + '-' + carrierHub.hubCode)
    })
  })

  onChange([...localSelectedCarrierHubs].join(','))
}

export const CarrierHubTreeDropdown = memo(function CarrierHubTreeDropdown({
  carrierHubs,
  selectedCarrierHubs,
  onChange,
  ...rest
}: CarrierHubTreeDropdownProps): JSX.Element {
  const data = makeCarrierHubTree(carrierHubs, selectedCarrierHubs)
  const _onChange = useMemo(() => _onChangeFactory(onChange), [onChange])

  return (
    <DropdownTreeSelect
      keepTreeOnSearch={true}
      keepChildrenOnSearch={true}
      data={data}
      onChange={_onChange}
      texts={{
        placeholder: 'Select Carrier / Hub',
        label: 'Select Carrier/Hub',
      }}
      {...rest}
    />
  )
},
arePropsEqual)
